import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  RefreshCw,
  Loader2,
  CheckCircle2,
  UserPlus,
  Trash2,
  AlertTriangle,
  List,
  CheckSquare,
  Search,
  Unlink,
  X,
} from 'lucide-react'
import {
  syncEasyshipLabels,
  getUnmatchedEasyshipLabels,
  getMatchedEasyshipLabels,
  matchEasyshipLabel,
  dismissEasyshipLabel,
  unpairEasyshipLabel,
  getAdminClients,
} from '../../lib/api'
import { ToastContainer, useToast } from '../../components/Toast'
import { format, parseISO } from 'date-fns'

function formatAUD(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(v ?? 0)
}

function formatDate(str) {
  if (!str) return '—'
  try {
    return format(parseISO(str), 'dd/MM/yy')
  } catch {
    return str
  }
}

// ── Per-row match cell ────────────────────────────────────────────────────────

function MatchCell({ label, clients, onMatched, onDismissed }) {
  const [mode, setMode]       = useState(null)  // null | 'match' | 'dismiss'
  const [selected, setSelected] = useState('')
  const { toasts, addToast }  = useToast()

  const matchMutation = useMutation({
    mutationFn: () => matchEasyshipLabel(label.id, selected),
    onSuccess: () => {
      setMode(null)
      onMatched()
    },
    onError: (err) => {
      addToast({
        type: 'error',
        message: `Match failed: ${err.response?.data?.detail || err.message}`,
      })
    },
  })

  const dismissMutation = useMutation({
    mutationFn: () => dismissEasyshipLabel(label.id),
    onSuccess: () => {
      setMode(null)
      onDismissed()
    },
    onError: (err) => {
      addToast({
        type: 'error',
        message: `Dismiss failed: ${err.response?.data?.detail || err.message}`,
      })
    },
  })

  const busy = matchMutation.isPending || dismissMutation.isPending

  if (mode === 'match') {
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <select
          autoFocus
          value={selected}
          onChange={e => setSelected(e.target.value)}
          className="text-xs border border-slate-300 rounded px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 max-w-[160px]"
        >
          <option value="">Select client…</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <button
          onClick={() => matchMutation.mutate()}
          disabled={!selected || busy}
          className="btn-primary text-xs py-1 px-2 disabled:opacity-50"
        >
          {matchMutation.isPending ? <Loader2 size={11} className="animate-spin" /> : 'Match'}
        </button>
        <button
          onClick={() => { setMode(null); setSelected('') }}
          className="text-xs text-slate-400 hover:text-slate-600 px-1"
        >
          ✕
        </button>
      </div>
    )
  }

  if (mode === 'dismiss') {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-red-600">Dismiss?</span>
        <button
          onClick={() => dismissMutation.mutate()}
          disabled={busy}
          className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
        >
          {dismissMutation.isPending ? <Loader2 size={11} className="animate-spin" /> : 'Yes'}
        </button>
        <button onClick={() => setMode(null)} className="text-xs text-slate-400 hover:text-slate-600 px-1">
          No
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => setMode('match')}
        className="flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600 transition-colors group"
      >
        <UserPlus size={13} className="group-hover:scale-110 transition-transform" />
        Match
      </button>
      <span className="text-slate-200">|</span>
      <button
        onClick={() => setMode('dismiss')}
        className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors group"
      >
        <Trash2 size={12} className="group-hover:scale-110 transition-transform" />
        Dismiss
      </button>
    </div>
  )
}

// ── Matched tab ───────────────────────────────────────────────────────────────

function MatchedTab() {
  const queryClient = useQueryClient()
  const { toasts: localToasts, addToast: localAddToast } = useToast()
  const [search, setSearch] = useState('')

  const { data: rows = [], isLoading, error: matchedError } = useQuery({
    queryKey: ['easyship', 'matched'],
    queryFn: getMatchedEasyshipLabels,
    refetchInterval: 120_000,
  })

  const unpair = useMutation({
    mutationFn: (returnLabelId) => unpairEasyshipLabel(returnLabelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['easyship'] })
      localAddToast({ type: 'success', message: 'Easyship label unmatched — back in Unmatched.' })
    },
    onError: (err) => localAddToast({
      type: 'error',
      message: `Unpair failed: ${err.response?.data?.detail || err.message}`,
    }),
  })

  function handleUnpair(row) {
    if (!row.return_label_id) return
    if (!window.confirm(
      `Unpair this Easyship label?\n\nClient: ${row.client_name || '—'}\nOrder: ${row.platform_order_number || '—'}\n\nIt will reappear in the Unmatched tab.`
    )) return
    unpair.mutate(row.return_label_id)
  }

  const filtered = rows.filter(r => {
    if (!search.trim()) return true
    const q = search.trim().toLowerCase()
    return (
      (r.client_name           || '').toLowerCase().includes(q) ||
      (r.platform_order_number || '').toLowerCase().includes(q) ||
      (r.order_reference       || '').toLowerCase().includes(q) ||
      (r.easyship_shipment_id  || '').toLowerCase().includes(q) ||
      (r.tracking_number       || '').toLowerCase().includes(q)
    )
  })

  const totalAUD       = filtered.reduce((s, r) => s + (Number(r.total_charge_aud) || 0), 0)
  const totalCharged   = filtered.reduce((s, r) => s + (Number(r.carrier_cost)     || 0), 0)
  const voidedCount    = filtered.filter(r => r.voided).length

  return (
    <>
      <ToastContainer toasts={localToasts} onRemove={() => {}} />

      <div className="space-y-4">
        <div className="card p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Search</label>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Client, order #, Easyship ID, tracking…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="input pl-8"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="text-xs text-slate-500 pb-2 space-x-2">
            <span>{filtered.length.toLocaleString()} matched
              {filtered.length !== rows.length && ` of ${rows.length.toLocaleString()}`}</span>
            <span className="text-slate-300">·</span>
            <span>Raw <span className="font-medium text-slate-700">{formatAUD(totalAUD)}</span></span>
            <span className="text-slate-300">·</span>
            <span>Charged <span className="font-medium text-slate-700">{formatAUD(totalCharged)}</span></span>
            {voidedCount > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-red-600">{voidedCount} voided</span>
              </>
            )}
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Label Date</th>
                  <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Matched On</th>
                  <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                  <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Easyship ID</th>
                  <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Order #</th>
                  <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Tracking</th>
                  <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider text-right">AUD Cost</th>
                  <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider text-right">Charged</th>
                  <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider text-center">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 10 }).map((_, j) => (
                        <td key={j} className="px-3 py-2"><div className="skeleton h-3 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : matchedError ? (
                  // Error before empty: on a failed fetch the `= []` default
                  // would render "no matched labels yet" and $0.00 totals.
                  <tr>
                    <td colSpan={10} className="py-12 text-center">
                      <AlertTriangle size={28} className="mx-auto text-red-500 mb-2" />
                      <p className="text-sm font-medium text-red-700">Couldn't load matched Easyship labels</p>
                      <p className="text-xs text-red-600 mt-1">
                        This is a load failure, not an empty list — matched labels exist
                        but aren't shown, and the totals above read $0.00.
                      </p>
                      <p className="text-xs text-slate-400 mt-1.5 font-mono">
                        {matchedError?.response?.data?.detail || matchedError?.message || 'Unknown error'}
                      </p>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-12 text-center">
                      <CheckSquare size={28} className="mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-400 text-sm">{search ? 'No results match your search' : 'No matched Easyship labels yet'}</p>
                    </td>
                  </tr>
                ) : filtered.map(r => {
                  const orderNum = r.order_reference || r.platform_order_number
                  return (
                    <tr key={r.id} className={`hover:bg-slate-50 transition-colors ${r.voided ? 'bg-red-50/40 opacity-60' : ''}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-500">{formatDate(r.label_created_at)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-500">{formatDate(r.matched_at)}</td>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{r.client_name || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 font-mono text-slate-400 whitespace-nowrap">{r.easyship_shipment_id}</td>
                      <td className="px-3 py-2 font-medium">
                        {orderNum
                          ? <span className="bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">{orderNum}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-500">{r.tracking_number || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {r.total_charge_aud != null ? formatAUD(r.total_charge_aud) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">
                        {r.carrier_cost != null ? formatAUD(r.carrier_cost) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {r.voided
                          ? <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700">Voided</span>
                          : <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">Matched</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => handleUnpair(r)}
                          disabled={unpair.isPending && unpair.variables === r.return_label_id}
                          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-purple-600 transition-colors disabled:opacity-50"
                          title="Unpair — send back to Unmatched"
                        >
                          {unpair.isPending && unpair.variables === r.return_label_id
                            ? <Loader2 size={11} className="animate-spin" />
                            : <Unlink size={11} />}
                          Unpair
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function UnmatchedEasyship() {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const [syncing, setSyncing] = useState(false)
  const [activeTab, setActiveTab] = useState('unmatched')

  const { data: labels = [], isLoading, error: labelsError } = useQuery({
    queryKey: ['easyship', 'unmatched'],
    queryFn: getUnmatchedEasyshipLabels,
    refetchInterval: 60_000,
  })

  const { data: clients = [] } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: getAdminClients,
  })

  const sortedClients = [...clients].sort((a, b) => a.name.localeCompare(b.name))

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ['easyship'] })
  }

  async function handleSync() {
    setSyncing(true)
    const now   = new Date()
    // Don't send a to_date — Easyship treats `created_at_to=YYYY-MM-DD` as
    // 00:00:00 of that day, which would silently drop everything created
    // today. The backend already handles a missing to_date by leaving the
    // upper bound unset (== "up to now"). Sending null here lets that path run.
    const from  = `${now.getFullYear()}-01-01`
    try {
      const result = await syncEasyshipLabels(from, null)
      if (result.success) {
        addToast({
          type: 'success',
          message: `Easyship sync complete — ${result.fetched} fetched, ${result.created} new, ${result.updated} updated.`,
          duration: 6000,
        })
      } else {
        const errSummary = result.errors.slice(0, 3).join(' · ')
        addToast({
          type: 'error',
          message: `Sync completed with errors: ${errSummary}`,
          duration: 8000,
        })
      }
      invalidateAll()
    } catch (err) {
      addToast({
        type: 'error',
        message: `Sync failed: ${err.response?.data?.detail || err.message}`,
        duration: 8000,
      })
    } finally {
      setSyncing(false)
    }
  }

  // Cost totals
  const totalAUD = labels.reduce((s, l) => s + (l.total_charge_aud ?? 0), 0)
  const totalWithFee = labels.reduce((s, l) => {
    const aud = l.total_charge_aud ?? 0
    return s + (aud > 0 ? aud * 1.10 + 2 : 0)
  }, 0)

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="space-y-6">

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Easyship Labels</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Return labels imported from Easyship — unmatched waiting for a client, or matched and ready to bill.
            </p>
          </div>
          <button onClick={handleSync} disabled={syncing} className="btn-secondary text-sm">
            {syncing
              ? <Loader2 size={14} className="animate-spin" />
              : <RefreshCw size={14} />}
            Sync Easyship
          </button>
        </div>

        {/* Tab toggle */}
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('unmatched')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'unmatched'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <List size={14} />
            Unmatched
          </button>
          <button
            onClick={() => setActiveTab('matched')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'matched'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <CheckSquare size={14} />
            Matched
          </button>
        </div>

        {activeTab === 'matched' && <MatchedTab />}

        {activeTab === 'unmatched' && <>

        {/* Info banner */}
        <div className="card border-blue-200 bg-blue-50 p-4 text-sm text-blue-700 space-y-1.5">
          <p className="font-medium flex items-center gap-2">
            <AlertTriangle size={15} className="text-blue-500" />
            How matching works
          </p>
          <ol className="list-decimal list-inside space-y-1 text-blue-600 text-xs">
            <li>Click <strong>Sync Easyship</strong> to pull the latest shipments from your Easyship account.</li>
            <li>For each label, select the client it belongs to and click <strong>Match</strong>.</li>
            <li>
              The label will appear in that client's <strong>Return Labels</strong> tab with the base fee applied
              (10% + A$2.00), plus any additional client-level return margins on top.
            </li>
            <li>Use <strong>Dismiss</strong> to permanently remove a label that does not need billing.</li>
          </ol>
        </div>

        {/* Stats — hidden on a failed fetch, because with `labels` defaulted to
            [] these cards would claim 0 unmatched, $0.00 cost and "All matched". */}
        {!isLoading && !labelsError && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="card p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Unmatched</p>
              <p className={`text-2xl font-bold ${labels.length > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                {labels.length}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Raw Cost (AUD)</p>
              <p className="text-2xl font-bold text-slate-700">{formatAUD(totalAUD)}</p>
            </div>
            <div className="card p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">After Base Fee</p>
              <p className="text-2xl font-bold text-slate-700">{formatAUD(totalWithFee)}</p>
              <p className="text-xs text-slate-400 mt-0.5">10% + A$2.00 per label</p>
            </div>
            <div className="card p-4 sm:col-span-1 col-span-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Status</p>
              {labels.length === 0 ? (
                <div className="flex items-center gap-2 mt-1">
                  <CheckCircle2 size={20} className="text-emerald-500" />
                  <span className="text-sm font-medium text-emerald-600">All matched</span>
                </div>
              ) : (
                <p className="text-sm font-medium text-orange-600 mt-1">Needs attention</p>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : labelsError ? (
            // Error before empty: react-query leaves `data` undefined on failure,
            // so the `= []` default would show the green "no unmatched labels"
            // all-clear while labels sit unbilled.
            <div className="flex flex-col items-center justify-center py-16 gap-2 px-6 text-center">
              <AlertTriangle size={32} className="text-red-500" />
              <p className="text-sm font-medium text-red-700">Couldn't load unmatched Easyship labels</p>
              <p className="text-xs text-red-600 max-w-md">
                This is a load failure, not an all-clear. There may be unmatched labels
                waiting to be attributed to a client — do not treat this as "nothing to do".
              </p>
              <p className="text-xs text-slate-400 font-mono">
                {labelsError?.response?.data?.detail || labelsError?.message || 'Unknown error'}
              </p>
            </div>
          ) : labels.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <CheckCircle2 size={32} className="text-emerald-400" />
              <p className="text-sm font-medium text-slate-600">No unmatched Easyship labels</p>
              <p className="text-xs text-slate-400">Sync to fetch new shipments from Easyship</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Easyship ID</th>
                    <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Order Number</th>
                    <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider text-right">USD Cost</th>
                    <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider text-right">AUD Cost</th>
                    <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider text-right">After Fee</th>
                    <th className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Match to store</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {labels.map((label) => {
                    const afterFee = label.total_charge_aud
                      ? label.total_charge_aud * 1.10 + 2
                      : null
                    const orderNum = label.order_reference || label.platform_order_number
                    return (
                      <tr key={label.id} className="hover:bg-orange-50/40 transition-colors">
                        <td className="px-3 py-2 whitespace-nowrap text-slate-500">
                          {formatDate(label.label_created_at)}
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-400 whitespace-nowrap">
                          {label.easyship_shipment_id}
                        </td>
                        <td className="px-3 py-2 font-medium">
                          {orderNum
                            ? <span className="bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">{orderNum}</span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                          {label.total_charge != null
                            ? `${label.currency} ${Number(label.total_charge).toFixed(2)}`
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                          {label.total_charge_aud != null
                            ? formatAUD(label.total_charge_aud)
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-orange-700">
                          {afterFee != null ? formatAUD(afterFee) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          <MatchCell
                            label={label}
                            clients={sortedClients}
                            onMatched={invalidateAll}
                            onDismissed={invalidateAll}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={4} className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">
                      Total ({labels.length} labels)
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                      {formatAUD(totalAUD)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-orange-700">
                      {formatAUD(totalWithFee)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        </>}

      </div>
    </>
  )
}
